/**
 * @fileoverview Contract verification utilities for EscapeHub SDK.
 *
 * This module provides the standard JSON input for Etherscan verification
 * of LaunchERC20 tokens deployed via the factory.
 *
 * @module @escapehub/token-creator/verification
 */

import standardJsonInput from './EscapeToken.standard-input.json';

/**
 * Standard JSON input for Etherscan contract verification.
 *
 * This is the Solidity compiler input format that includes all source files
 * and compiler settings needed to verify LaunchERC20 tokens on Etherscan.
 *
 * @example
 * ```typescript
 * import { STANDARD_JSON_INPUT, SOLC_VERSION, CONTRACT_NAME } from '@escapehub/token-creator';
 *
 * // Submit to Etherscan API
 * const response = await fetch('https://api.etherscan.io/api', {
 *   method: 'POST',
 *   body: new URLSearchParams({
 *     module: 'contract',
 *     action: 'verifysourcecode',
 *     sourceCode: JSON.stringify(STANDARD_JSON_INPUT),
 *     codeformat: 'solidity-standard-json-input',
 *     contractname: CONTRACT_NAME,
 *     compilerversion: SOLC_VERSION,
 *     // ... other params
 *   }),
 * });
 * ```
 */
export const STANDARD_JSON_INPUT = standardJsonInput;

/**
 * Solidity compiler version used to compile LaunchERC20.
 * Must match when verifying on Etherscan.
 */
export const SOLC_VERSION = 'v0.8.28+commit.7893614a';

/**
 * Contract name in the format expected by Etherscan.
 * Format: "path/to/Contract.sol:ContractName"
 */
export const CONTRACT_NAME = 'src/LaunchERC20.sol:LaunchERC20';

/**
 * Etherscan API URLs for different chains.
 * Maps chain ID to the verification API endpoint.
 */
export const ETHERSCAN_API_URLS: Record<number, string> = {
  // Mainnets
  1: 'https://api.etherscan.io/api',
  56: 'https://api.bscscan.com/api',
  137: 'https://api.polygonscan.com/api',
  42161: 'https://api.arbiscan.io/api',
  42170: 'https://api-nova.arbiscan.io/api',
  10: 'https://api-optimistic.etherscan.io/api',
  8453: 'https://api.basescan.org/api',
  43114: 'https://api.snowtrace.io/api',
  59144: 'https://api.lineascan.build/api',
  81457: 'https://api.blastscan.io/api',
  534352: 'https://api.scrollscan.com/api',
  5000: 'https://api.mantlescan.xyz/api',
  42220: 'https://api.celoscan.io/api',
  1284: 'https://api-moonbeam.moonscan.io/api',
  146: 'https://api.sonicscan.org/api',

  // Testnets
  11155111: 'https://api-sepolia.etherscan.io/api',
  97: 'https://api-testnet.bscscan.com/api',
  80002: 'https://api-amoy.polygonscan.com/api',
  421614: 'https://api-sepolia.arbiscan.io/api',
  11155420: 'https://api-sepolia-optimistic.etherscan.io/api',
  84532: 'https://api-sepolia.basescan.org/api',
  43113: 'https://api-testnet.snowtrace.io/api',
  534351: 'https://api-sepolia.scrollscan.com/api',
  5003: 'https://api-sepolia.mantlescan.xyz/api',
};

/**
 * Get the Etherscan API URL for a given chain ID.
 *
 * @param chainId - The chain ID
 * @returns The API URL or undefined if not supported
 */
export function getEtherscanApiUrl(chainId: number): string | undefined {
  return ETHERSCAN_API_URLS[chainId];
}
